/** Port von `Client/Components/Base/IwmBasePageView.razor`. */

import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';

import type { ViewApi } from '@/api/apiViewBase';
import { viewApi } from '@/api/services/viewApi';
import { createViewDataSource, type ViewDataSource } from '@/api/viewDataSource';
import { useAuth } from '@/auth/useAuth';
import type { CellTemplateMap } from '@/components/columns/types';
import { buildColumns } from '@/components/grid/columnsFromViewFields';
import type { DataGridHandle, GridColumn } from '@/components/grid/types';
import { useDialogService } from '@/components/ui';
import type { BaseEntity, ChinaAppSysGroupView, ChinaAppSysView } from '@/types/entities';
import type { EntityTypes } from '@/types/enums';
import { buildExportFileName, saveAs } from '@/utils/file';

/** Texte aus `Delete()`. */
const DELETE_TITLE = 'Datensatz entfernen';
const DELETE_CONFIRM = 'Möchten Sie die ausgewählten Datensätze wirklich entfernen?';
const DELETE_NO_SELECTION = 'Es wurden keine Datensätze ausgewählt.';
/** `pageSize: 999999999` aus `OnExcelExport`. */
const EXPORT_PAGE_SIZE = 999999999;

export interface ViewPageOptions<T extends BaseEntity> {
  viewId: number;
  entity: EntityTypes;
  api: ViewApi<T>;
  cellTemplates: CellTemplateMap<T>;
  editTemplates: CellTemplateMap<T>;
  /** Pendant zu `AfterRefresh()` — läuft nach dem Laden von View, Rechten und Spalten. */
  afterRefresh?(view: ChinaAppSysView | null): void | Promise<void>;
}

export interface ViewPage<T extends BaseEntity> {
  view: ChinaAppSysView | null;
  permissions: ChinaAppSysGroupView | null;
  columns: GridColumn<T>[];
  dataSource: ViewDataSource<T>;
  gridRef: RefObject<DataGridHandle<T> | null>;
  selectedId: number | null;
  setSelectedId(id: number | null): void;
  excelExportRunning: boolean;
  /** Speichert eine geänderte Zelle und aktualisiert die Zeile im Grid. */
  saveCell(item: T, columnName: string): Promise<void>;
  /** Löscht alle ausgewählten Datensätze nach Rückfrage. */
  deleteSelected(): Promise<void>;
  /** Exportiert die aktuelle Filterung nach XLSX. */
  exportExcel(): Promise<void>;
}

export function useViewPage<T extends BaseEntity>(options: ViewPageOptions<T>): ViewPage<T> {
  const { api, entity, viewId } = options;
  // eslint-disable-next-line @typescript-eslint/unbound-method -- stabile Closure, keine Methode
  const { getViewPermissions } = useAuth();
  const dialogService = useDialogService();
  const gridRef = useRef<DataGridHandle<T> | null>(null);

  const [view, setView] = useState<ChinaAppSysView | null>(null);
  const [permissions, setPermissions] = useState<ChinaAppSysGroupView | null>(null);
  const [columns, setColumns] = useState<GridColumn<T>[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [excelExportRunning, setExcelExportRunning] = useState(false);

  // `Grid.Qry` wird beim Wechsel der ViewId neu gesetzt; das Pendant ist eine neue Datenquelle.
  const dataSource = useMemo(() => createViewDataSource(api, () => viewId), [api, viewId]);

  // Der Altclient lädt allein beim Wechsel der ViewId nach. Spalten-Vorlagen, `afterRefresh`
  // und die Rechteabfrage wechseln in React bei jedem Rendern ihre Identität und dürfen
  // deshalb kein erneutes Laden auslösen.
  const latest = useRef(options);
  latest.current = options;
  const permissionsOf = useRef(getViewPermissions);
  permissionsOf.current = getViewPermissions;

  // Port von `Refresh(init: true)`.
  useEffect(() => {
    let active = true;

    const refresh = async (): Promise<void> => {
      const loadedView = await viewApi.getEntry(viewId);
      if (!active) return;
      setView(loadedView);
      setPermissions(permissionsOf.current(viewId));

      // `GetColumns` liest die Felder über `ApiViewService.GetFields`, nicht aus `View.Fields`.
      const fields = await viewApi.getFields(viewId);
      if (!active) return;
      setColumns(
        buildColumns<T>({
          fields,
          entity,
          cellTemplates: latest.current.cellTemplates,
          editTemplates: latest.current.editTemplates,
        }),
      );

      await latest.current.afterRefresh?.(loadedView);
    };

    void refresh();
    return () => {
      active = false;
    };
  }, [entity, viewId]);

  // Port von `OnSave`; die entitätsspezifische Fehlerbehandlung liegt in den Seiten.
  const saveCell = useCallback(
    async (item: T, columnName: string): Promise<void> => {
      const result = await api.put(viewId, item.Id, item, columnName);
      gridRef.current?.setRowData(result.Id, result);
    },
    [api, viewId],
  );

  // Port von `Delete()`.
  const deleteSelected = useCallback(async (): Promise<void> => {
    const records = gridRef.current?.getSelectedRecords() ?? [];
    if (records.length === 0) {
      await dialogService.alert(DELETE_NO_SELECTION, DELETE_TITLE);
      return;
    }
    if (!(await dialogService.confirm(DELETE_CONFIRM, DELETE_TITLE))) return;
    for (const record of records) {
      await api.remove(viewId, record.Id);
    }
    await gridRef.current?.refresh();
  }, [api, dialogService, viewId]);

  // Port von `OnExcelExport` + `ExcelExport`.
  const exportExcel = useCallback(async (): Promise<void> => {
    setExcelExportRunning(true);
    try {
      const bytes = await api.exportXlsx(viewId, {
        pageSize: EXPORT_PAGE_SIZE,
        whereQuery: dataSource.previousWhereQuery,
      });
      saveAs(buildExportFileName(), bytes);
    } catch {
      // Der Altclient meldet Exportfehler nicht.
    } finally {
      setExcelExportRunning(false);
    }
  }, [api, dataSource, viewId]);

  return {
    view,
    permissions,
    columns,
    dataSource,
    gridRef,
    selectedId,
    setSelectedId,
    excelExportRunning,
    saveCell,
    deleteSelected,
    exportExcel,
  };
}

export default useViewPage;
